import classNames from "classnames";
import groupBy from "lodash.groupby";
import sortBy from "lodash.sortby";
import React, { useState } from "react";
import GetText from "../../i18n/GetText";
import ChevronDownIcon from "../../icons/ChevronDownIcon";
import ChevronRightIcon from "../../icons/ChevronRightIcon";
import { SanityCountry, SanityGlobalRegion } from "../../model/common";
import PlainButton from "../Button/PlainButton/PlainButton";
import Checkbox from "../Inputs/Checkbox/Checkbox";
import { FilterType } from "../Listing/filters";
import Text from "../Text/Text";
import styles from "./FilterTree.module.scss";

export interface GlobalRegionExpansionState {
  [id: string]: boolean;
}

const LocationFilterTree = ({
  filterData,
  isSelected,
  setSelected,
  titleId
}: Props) => {
  // We keep expansion state for each global region here.
  // Selection state is external.
  const globalRegions = sortBy(
    countriesByGlobalRegion(filterData),
    x => x.name
  );

  const [expandedList, setExpandedList] = useState<GlobalRegionExpansionState>(
    {}
  );
  const setExpanded = (globalRegion: SanityGlobalRegion, state: boolean) =>
    setExpandedList({
      ...expandedList,
      [globalRegion._id]: state
    });
  const isExpanded = (globalRegion: SanityGlobalRegion): boolean =>
    !!expandedList[globalRegion._id];

  return (
    <>
      <Text variant="filterHeading">
        <GetText id={titleId} />
      </Text>
      <ul className={styles.primaryList}>
        {globalRegions.map((globalRegion: GlobalRegionWithCountries) => (
          <React.Fragment key={globalRegion._id}>
            <li className={styles.primaryListItem}>
              <>
                <Checkbox
                  id={`checkbox-${globalRegion._id}`}
                  label={
                    <Text variant="filter">
                      <GetText id={globalRegion.name} />
                    </Text>
                  }
                  value={globalRegion._id}
                  onChange={e => {
                    const checked = e.currentTarget.checked;
                    const changes: Record<string, boolean> = {};
                    globalRegion.countries.forEach(
                      c => (changes[c._id] = false)
                    );
                    changes[globalRegion._id] = checked;
                    setSelected(changes);
                    if (checked) {
                      setExpanded(globalRegion, true);
                    }
                  }}
                  checked={
                    isSelected(globalRegion) ||
                    globalRegion.countries.every(isSelected)
                  }
                  indeterminate={
                    !isSelected(globalRegion) &&
                    globalRegion.countries.some(isSelected) &&
                    !globalRegion.countries.every(isSelected)
                  }
                  renderRight={
                    <PlainButton
                      onClick={() =>
                        setExpanded(globalRegion, !isExpanded(globalRegion))
                      }
                      className={styles.chevronLink}
                      aria-label={
                        isExpanded(globalRegion) ? "Collapse" : "Expand"
                      }
                      aria-expanded={isExpanded(globalRegion)}
                      id={globalRegion._id}
                    >
                      {expandedList[globalRegion._id] ? (
                        <ChevronDownIcon className={styles.chevronIcon} />
                      ) : (
                        <ChevronRightIcon
                          className={classNames(
                            styles.chevronIcon,
                            styles.chevronIconRight
                          )}
                        />
                      )}
                    </PlainButton>
                  }
                  style={{ height: 35 }}
                />
              </>
            </li>
            {expandedList[globalRegion._id] && (
              <ul
                className={styles.secondaryList}
                aria-labelledby={globalRegion._id}
              >
                {globalRegion.countries.map((country: FilterType) => (
                  <li key={country._id} className={styles.secondaryListItem}>
                    <Checkbox
                      id={`checkbox-${country._id}`}
                      label={
                        <Text variant="filter">
                          <GetText id={country.name} />
                        </Text>
                      }
                      value={country._id}
                      onChange={e => {
                        const checked = e.currentTarget.checked;
                        setSelected({
                          [country._id]: checked,
                          [globalRegion._id]: false
                        });
                      }}
                      checked={isSelected(country)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </React.Fragment>
        ))}
      </ul>
    </>
  );
};

export default LocationFilterTree;

interface Props {
  titleId: string;
  filterData: FilterType[];
  isSelected: (filter: FilterType) => boolean;
  setSelected: (selections: Record<string, boolean>) => void;
}

export interface GlobalRegionWithCountries extends SanityGlobalRegion {
  countries: SanityCountry[];
}

// Countries have a globalRegion field but we want a tree rooted at globalRegion.
const countriesByGlobalRegion = (
  filterData: FilterType[]
): GlobalRegionWithCountries[] => {
  const globalRegionById: { [id: string]: any } = {};
  filterData.forEach(country => {
    if (country.globalRegion) {
      globalRegionById[country.globalRegion._id] = country.globalRegion;
    }
  });
  const byGlobalRegionId = groupBy(filterData, c => c.globalRegion?._id);
  const result = Object.values(globalRegionById).map(globalRegion => {
    const countriesForGlobalRegion = byGlobalRegionId[globalRegion._id];
    return {
      ...globalRegion,
      countries: sortBy(countriesForGlobalRegion, c => c.name)
    };
  });
  return result;
};
